import { BooleanField, NumberField, RadioField, TextField } from "@hex-insights/forms";
import { ServiceFormValues } from "../../FormUtilities";
import { ClassYearSelectField } from "../ClassYear";
import { BaseFieldProps } from "../shared";

/**
 * Generic props for fields of the Service model.
 */
type FieldProps<K extends keyof ServiceFormValues.Base = keyof ServiceFormValues.Base> = BaseFieldProps<
	Pick<ServiceFormValues.Base, K>
>;

/**
 * Generic props for fields of the Service model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof ServiceFormValues.Detail = keyof ServiceFormValues.Detail> = BaseFieldProps<
	Pick<ServiceFormValues.Detail, K>
>;

/**
 * Field component for the `name` field of the Service model.
 */
export function Name({ formState, ...props }: FieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `description` field of the Service model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional {...props} />;
}

/**
 * Field component for the `category` field of the Service model.
 */
export function Category({ formState, ...props }: FieldProps<"category">) {
	return (
		<RadioField
			formState={formState}
			name="category"
			options={ServiceFormValues.categoryOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `isActive` field of the Service model.
 */
export function IsActive({ formState, ...props }: DetailFieldProps<"isActive">) {
	return <BooleanField formState={formState} name="isActive" {...props} />;
}

/**
 * Field component for the `price` field of the Service model.
 */
export function Price({ formState, ...props }: FieldProps<"price">) {
	return <NumberField formState={formState} name="price" validationUnit={1} min={0} unit={100} {...props} />;
}

export namespace ClassYears {
	export type SelectProps = FieldProps<"classYearIDs"> &
		Omit<ClassYearSelectField.MultiSelectProps<ServiceFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `classYears` edge of the Service model.
	 */
	export function Select({
		formState,
		hint = "Restricts this service to only students of the specified class years. To allow all students, leave this field blank.",
		...props
	}: SelectProps) {
		return <ClassYearSelectField.MultiSelect formState={formState} name="classYearIDs" hint={hint} {...props} />;
	}
}
